/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  GearIcon,
} from '@/shared/ui/radix-icons';
import type { AgendaView } from '@features/agenda/utils/agenda-dates';
import { Text, UnstyledButton } from '@/shared/ui';

interface AgendaMobileHeaderProps {
  view: AgendaView;
  /** Full month name for the current anchor (e.g. "Junho"). */
  monthLabel: string;
  onOpenMonth: () => void;
  onNavigate: (direction: 1 | -1) => void;
  onViewChange: (view: AgendaView) => void;
  onOpenSettings?: () => void;
}

/**
 * The two ways to read the agenda on a phone.
 *
 * Week and month are gone. Both are grids built for a screen that can show
 * seven columns at once; squeezed into 400px a week column is 50px wide and a
 * month cell is a dot you cannot read a title in, so neither answered the
 * question somebody opens the agenda to ask. The day screen carries a week
 * strip of its own, which is the part of "week" that was worth keeping, and
 * the list is there for reading several days in a row.
 */
const MOBILE_VIEWS: AgendaView[] = ['day', 'list'];

/**
 * Compact, touch-first agenda header for mobile: a month button that opens a
 * date jumper, period chevrons, and a full-width day/3-day/week/month switch.
 *
 * @param props Current view, month label, and navigation handlers.
 * @returns Rendered mobile agenda header.
 */
export const AgendaMobileHeader: React.FC<AgendaMobileHeaderProps> = ({
  view,
  monthLabel,
  onOpenMonth,
  onNavigate,
  onViewChange,
  onOpenSettings,
}) => {
  const { t } = useTranslation('features/agenda');
  const chevronBtn =
    'flex h-9 w-9 items-center justify-center rounded-[var(--radius-md)] border border-[var(--border-subtle)] text-[var(--text-secondary)] transition-colors active:bg-[var(--surface-secondary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--seed-base)]';

  return (
    <div className="shrink-0 border-b border-[var(--border-subtle)] bg-[var(--surface-primary)] px-(--space-4) pt-(--space-3) pb-(--space-2)">
      <div className="mb-(--space-2) flex items-center justify-between gap-(--space-2)">
        <UnstyledButton
          type="button"
          aria-label={t('mobile.open_month')}
          onClick={onOpenMonth}
          className="flex min-w-0 items-center gap-(--space-2) rounded-[var(--radius-md)] px-(--space-2) py-(--space-1-5) text-left transition-colors active:bg-[var(--surface-secondary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--seed-base)]"
        >
          <CalendarIcon width={18} height={18} className="shrink-0 text-[var(--seed-base)]" />
          <Text component="span" className="truncate text-(--text-h6) font-bold capitalize text-[var(--text-primary)]">
            {monthLabel}
          </Text>
        </UnstyledButton>
        <div className="flex shrink-0 items-center gap-(--space-1-5)">
          {onOpenSettings && (
            <UnstyledButton
              type="button"
              aria-label={t('mobile.open_settings')}
              onClick={onOpenSettings}
              className={chevronBtn}
            >
              <GearIcon width={16} height={16} />
            </UnstyledButton>
          )}
          <UnstyledButton
            type="button"
            aria-label={t('toolbar.previous')}
            onClick={() => onNavigate(-1)}
            className={chevronBtn}
          >
            <ChevronLeftIcon width={16} height={16} />
          </UnstyledButton>
          <UnstyledButton
            type="button"
            aria-label={t('toolbar.next')}
            onClick={() => onNavigate(1)}
            className={chevronBtn}
          >
            <ChevronRightIcon width={16} height={16} />
          </UnstyledButton>
        </div>
      </div>
      {/*
        Columns derived from the list, not hardcoded. It said `grid-cols-4`
        while the phone offered four views; dropping week and month left the
        two survivors sharing half the row with an empty half beside them.
      */}
      <div
        role="tablist"
        data-tour="agenda-views"
        className="grid overflow-hidden rounded-[var(--radius-md)] border border-[var(--border-subtle)]"
        style={{ gridTemplateColumns: `repeat(${MOBILE_VIEWS.length}, minmax(0, 1fr))` }}
      >
        {MOBILE_VIEWS.map((v) => {
          const selected = view === v;
          return (
            <UnstyledButton
              key={v}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onViewChange(v)}
              className="min-h-11 py-(--space-2) text-(--text-xs) transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--seed-base)]"
              style={
                selected
                  ? { background: 'var(--seed-base)', color: '#fff', fontWeight: 700 }
                  : { color: 'var(--text-secondary)', fontWeight: 500 }
              }
            >
              {t(`view.${v}`)}
            </UnstyledButton>
          );
        })}
      </div>
    </div>
  );
};
